import { http } from '@/lib/http';
import type { Membership } from '@/types';

/**
 * Shape returned by `GET /users/me` — mirrors `controller.meResponse` in
 * the vizitpro-api swagger spec.
 */
export interface MeResponse {
  avatar_url: string | null;
  businesses: Membership[];
  created_at: string;
  email: string | null;
  firebase_uid: string | null;
  full_name: string | null;
  id: string;
  phone: string | null;
  preferred_language: string | null;
  updated_at: string;
}

/**
 * React Query cache key for the current user. Shared so other features
 * (onboarding completion, business switching, profile edits) can invalidate
 * or refetch it after a mutation that changes `/users/me` server-side.
 */
export const ME_QUERY_KEY = ['me'] as const;

/**
 * Calls `GET /users/me`. Returns the authenticated user's row plus the
 * businesses (and role/permissions at each) they belong to.
 */
export async function getMe(): Promise<MeResponse> {
  const { data } = await http.get<MeResponse>('/users/me');
  return data;
}

/**
 * Request body for `PATCH /users/me` — mirrors
 * `controller.updateMeRequest` in the vizitpro-api swagger spec. All fields
 * are optional.
 */
export interface UpdateMeRequest {
  full_name?: string;
  phone?: string;
  avatar_url?: string;
  preferred_language?: string;
}

/**
 * Mirrors `repo.User` in the vizitpro-api swagger spec — the plain user row
 * returned by `PATCH /users/me` (no `businesses` field, unlike
 * `MeResponse`).
 */
export interface UserDto {
  avatar_url: string | null;
  created_at: string;
  email: string | null;
  firebase_uid: string | null;
  full_name: string | null;
  id: string;
  phone: string | null;
  preferred_language: string | null;
  updated_at: string;
}

/** Calls `PATCH /users/me`. */
export async function updateMe(body: UpdateMeRequest): Promise<UserDto> {
  const { data } = await http.patch<UserDto>('/users/me', body);
  return data;
}
